"use client";

import type { PeBandRow } from "@/lib/types/financial-snapshot";
import { axisLabel, niceScale, CHART_PADDING, CHART_HEIGHT, YAxis, ChartSvg } from "../ChartAxes";
import { useChartHover } from "../hover/use-chart-hover";
import { HoverHitAreas } from "../hover/HoverHitAreas";
import { HoverCrosshair } from "../hover/HoverCrosshair";
import { ChartTooltip } from "../hover/ChartTooltip";
import type { HoverPoint } from "../hover/types";

const VIEW_WIDTH = 640;

const fmtMoney = (v: number) => {
  const abs = Math.abs(v);
  if (abs >= 1e3) return `$${(v / 1e3).toFixed(2)}K`;
  return `$${v.toFixed(2)}`;
};

/**
 * Splits rows into consecutive runs where band is populated.
 * Each run becomes its own filled polygon + dashed mid polyline.
 */
function bandRuns(rows: PeBandRow[]): Array<{ start: number; end: number }> {
  const runs: Array<{ start: number; end: number }> = [];
  let current: { start: number; end: number } | null = null;
  rows.forEach((r, i) => {
    const hasBand = r.peLow != null && r.peHigh != null;
    if (hasBand) {
      if (!current) current = { start: i, end: i };
      else current.end = i;
    } else if (current) {
      runs.push(current);
      current = null;
    }
  });
  if (current) runs.push(current);
  return runs;
}

export function PeBandChart({ rows }: { rows: PeBandRow[] }) {
  const { containerRef, activeIndex, handlePointerMove, handlePointerLeave, handlePointerUp } = useChartHover();

  if (rows.length < 2) return null;

  const prices = rows.map((r) => r.price);
  const bandVals = rows.flatMap((r) => [r.peLow, r.peHigh].filter((v): v is number => v != null));
  const maxVal = Math.max(...prices, ...bandVals);
  const minVal = Math.min(...prices, ...bandVals, 0);
  const scale = niceScale(maxVal, minVal);

  const plotTop = CHART_PADDING.top;
  const plotBottom = CHART_HEIGHT - CHART_PADDING.bottom;
  const plotHeight = plotBottom - plotTop;
  const yScale = (v: number) => plotBottom - ((v - scale.min) / (scale.max - scale.min)) * plotHeight;
  const xScale = (i: number) =>
    CHART_PADDING.left + (i / (rows.length - 1)) * (VIEW_WIDTH - CHART_PADDING.left - CHART_PADDING.right);

  const runs = bandRuns(rows);

  const pricePoints = rows.map((r, i) => `${xScale(i)},${yScale(r.price)}`).join(" ");

  const anchors = rows.map((_, i) => xScale(i));

  // PE band rows are indexed by date; use that as the tooltip heading so users
  // can correlate price with fundamentals. ChartTooltip renders "n/a" for null
  // series values automatically, so negative-EPS quarters with null bands just work.
  const points: HoverPoint[] = rows.map((row) => ({
    period: row.date,
    series: [
      { label: "Price", value: row.price, format: fmtMoney, colorClass: "bg-foreground" },
      { label: "PE low (p25)", value: row.peLow, format: fmtMoney, colorClass: "bg-blue" },
      { label: "PE mid (p50)", value: row.peMid, format: fmtMoney, colorClass: "bg-blue" },
      { label: "PE high (p75)", value: row.peHigh, format: fmtMoney, colorClass: "bg-blue" },
    ],
  }));

  return (
    <div ref={containerRef} className="relative">
      <ChartSvg viewBox={`0 0 ${VIEW_WIDTH} ${CHART_HEIGHT}`}>
        <YAxis ticks={scale.ticks} scale={yScale} format={axisLabel} width={VIEW_WIDTH} />

        {/* Band polygons — one per run */}
        {runs.map((run, idx) => {
          const topPath = rows
            .slice(run.start, run.end + 1)
            .map((r, j) => `${xScale(run.start + j)},${yScale(r.peHigh as number)}`)
            .join(" ");
          const bottomPath = rows
            .slice(run.start, run.end + 1)
            .reverse()
            .map((r, j) => `${xScale(run.end - j)},${yScale(r.peLow as number)}`)
            .join(" ");
          return (
            <polygon
              key={idx}
              points={`${topPath} ${bottomPath}`}
              className="fill-blue"
              fillOpacity={0.15}
            />
          );
        })}

        {/* p50 dashed line — one polyline per run */}
        {runs.map((run, idx) => {
          const pts = rows
            .slice(run.start, run.end + 1)
            .map((r, j) => `${xScale(run.start + j)},${yScale(r.peMid as number)}`)
            .join(" ");
          return (
            <polyline
              key={idx}
              fill="none"
              className="stroke-blue"
              strokeOpacity={0.6}
              strokeWidth={1}
              strokeDasharray="4 4"
              points={pts}
            />
          );
        })}

        {/* Price line — always continuous */}
        <polyline fill="none" className="stroke-foreground" strokeWidth={2} points={pricePoints} />

        <g transform={`translate(${CHART_PADDING.left}, 4)`}>
          <rect width={12} height={8} className="fill-blue" fillOpacity={0.15} />
          <text x={16} y={8} fontSize={10} fill="currentColor" fillOpacity={0.7}>PE Band (25-75%)</text>
          <rect x={120} width={8} height={8} className="fill-foreground" />
          <text x={132} y={8} fontSize={10} fill="currentColor" fillOpacity={0.7}>Price</text>
        </g>

        {activeIndex != null && <HoverCrosshair x={anchors[activeIndex]} plotTop={plotTop} plotBottom={plotBottom} />}
        <HoverHitAreas
          anchors={anchors}
          viewBoxWidth={VIEW_WIDTH}
          plotTop={plotTop}
          plotBottom={plotBottom}
          onActivate={handlePointerMove}
          onLeave={handlePointerLeave}
          onTap={handlePointerUp}
        />
      </ChartSvg>
      {activeIndex != null && (
        <ChartTooltip point={points[activeIndex]} anchorPct={(anchors[activeIndex] / VIEW_WIDTH) * 100} />
      )}
    </div>
  );
}
